import MenuItemCard from "./components/menu-item-card";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMainContext } from "../../context";
import { SessionService } from "../../services/persistService";
import CEPModal from "./components/cep-modal";
import { IoRestaurant, IoSearch } from "react-icons/io5";
import { ROUTES } from "../../routes";

const MenuPage = () => {
  const {
    categories,
    restaurant,
    findDeliveryPrice,
    setRestaurantDeliveryPrice,
    setProduct,
    search,
    hasInitialized,
  } = useMainContext();
  const [activeCategory, setActiveCategory] = useState(0);
  const [cepModalIsOpen, setCepModalIsOpen] = useState(false);
  const [restaurantServesAtAddress, setRestaurantServesAtAddress] =
    useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  let isBalcony = searchParams.get("balcony");

  const getRestaurantDeliveryPrice = useCallback(
    async (cep) => {
      SessionService.addItem("cep", cep);

      await findDeliveryPrice();
      setRestaurantServesAtAddress(
        SessionService.getItem("restaurantServesAtAddress"),
      );
    },
    [findDeliveryPrice],
  );

  const handleItemCardClick = (product) => {
    setProduct(product);
    navigate(ROUTES.ITEM(restaurant.id, product.id));
  };

  const filteredCategories = useMemo(() => {
    const clearedSearch = search.trim().toLowerCase();

    if (clearedSearch === "") {
      return categories;
    }

    const filtered = [];

    categories.forEach((category) => {
      const filteredProducts = category.products.filter((product) =>
        product.name.toLowerCase().includes(clearedSearch),
      );

      if (filteredProducts.length > 0) {
        filtered.push({ ...category, products: filteredProducts });
      }
    });

    return filtered;
  }, [categories, search]);

  useEffect(() => {}, [categories, search]);

  useEffect(() => {
    const firstNotBlocked = filteredCategories.find(
      (category) => category.status !== "block",
    );
    setActiveCategory(firstNotBlocked || filteredCategories[0]);
  }, [filteredCategories]);

  useEffect(() => {
    if (!restaurant.id) {
      return;
    }

    if (isBalcony) {
      setRestaurantDeliveryPrice(restaurant.delivery_prices[0]);
      SessionService.addItem("restaurantServesAtAddress", true);
      SessionService.addItem("isBalcony", true);
      setRestaurantServesAtAddress(true);
    } else {
      const localCEP = SessionService.getItem("cep");

      if (!localCEP) {
        return setCepModalIsOpen(true);
      }

      getRestaurantDeliveryPrice(localCEP);
    }
  }, [
    restaurant,
    getRestaurantDeliveryPrice,
    isBalcony,
    setRestaurantDeliveryPrice,
  ]);

  if (!hasInitialized) {
    return (
      <div className="p-10 w-full flex-grow flex flex-col items-center justify-center">
        <IoRestaurant className="w-16 h-16 text-gray-300 animate-bounce" />
        <span className="mt-4 text-gray-500 text-md">
          Carregando cardápio...
        </span>
      </div>
    );
  }

  return (
    <>
      {filteredCategories && (
        <div className="p-4 md:p-8 flex-grow">
          {filteredCategories.length > 0 && (
            <div className="flex flex-row flex-wrap gap-2 mb-6">
              {filteredCategories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCategory(category)}
                  className={[
                    "px-4 py-2 rounded-xl whitespace-nowrap  transition-colors",
                    activeCategory?.id === category.id
                      ? "font-bold bg-primary-500 hover:bg-primary-400 text-white"
                      : "font-medium hover:bg-gray-300 text-gray-800",
                    category.status === "block" &&
                      "opacity-50 cursor-not-allowed",
                  ].join(" ")}
                >
                  {category.name}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeCategory && (
              <>
                <div className="md:col-span-2 border-l-4 border-primary-500 pl-4 mb-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <h2 className="text-2xl font-black uppercase text-black">
                      {activeCategory.name}
                    </h2>
                    {activeCategory.status === "block" && (
                      <span className="text-sm bg-secondary-500 text-black py-0.5 px-2 rounded-full">
                        Não disponível agora
                      </span>
                    )}
                  </div>
                  {activeCategory.description &&
                    activeCategory.description.toLowerCase() !==
                      activeCategory.name.toLowerCase() && (
                      <p className="text-sm text-gray-600 m-0">
                        {activeCategory.description}
                      </p>
                    )}
                </div>

                {activeCategory.products.map((product, index) => (
                  <MenuItemCard
                    key={index}
                    product={product}
                    restaurant={restaurant}
                    activeCategory={activeCategory}
                    categories={categories}
                    onPress={() => handleItemCardClick(product)}
                  />
                ))}
              </>
            )}

            {(!activeCategory || activeCategory.products.length === 0) && (
              <div className="flex flex-col items-center justify-center text-gray-600 min-h-[45vh] md:col-span-2">
                <IoSearch className="w-10 h-10 mb-3 opacity-60 animate-bounce" />
                <p className="text-sm">Nenhum produto encontrado</p>
              </div>
            )}
          </div>
        </div>
      )}

      <CEPModal
        isModalOpen={cepModalIsOpen}
        setModalOpen={setCepModalIsOpen}
        restaurant={restaurant}
        onConfirmCep={getRestaurantDeliveryPrice}
        restaurantServesAtAddress={restaurantServesAtAddress}
      />
    </>
  );
};

export default MenuPage;
